import { useState, useEffect } from"react";
import { useParams, useNavigate } from"react-router-dom";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { Checkbox } from"@/components/ui/checkbox";
import { Progress } from"@/components/ui/progress";
import { 
 ArrowLeft, Plus, Trash2, Edit2, CheckCircle2, 
 Building2, Layers, Filter, CheckSquare, Circle,
 ChevronDown, ChevronRight, RefreshCw
} from"lucide-react";
import { toast } from"sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const JobSubtasksPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const { projectId, jobId } = useParams();
 const navigate = useNavigate();
 
 const [job, setJob] = useState(null);
 const [subtasks, setSubtasks] = useState([]);
 const [units, setUnits] = useState([]);
 const [buildings, setBuildings] = useState([]);
 const [summary, setSummary] = useState(null);
 const [trackingMode, setTrackingMode] = useState("by_unit");
 const [loading, setLoading] = useState(true);
 
 // Filters
 const [selectedBuilding, setSelectedBuilding] = useState("all");
 const [selectedFloor, setSelectedFloor] = useState("all");
 
 // Dialogs
 const [addSubtaskOpen, setAddSubtaskOpen] = useState(false);
 const [editSubtaskOpen, setEditSubtaskOpen] = useState(false);
 const [editingSubtask, setEditingSubtask] = useState(null);
 const [newSubtaskName, setNewSubtaskName] = useState("");
 const [applyToAllProjects, setApplyToAllProjects] = useState(true);
 const [newSubtaskWeight, setNewSubtaskWeight] = useState("");
 
 // Expanded floors
 const [expandedFloors, setExpandedFloors] = useState({});

 useEffect(() => {
 loadData();
 }, [jobId, selectedBuilding, selectedFloor]);

 const loadData = async () => {
 try {
 setLoading(true);
 const token = localStorage.getItem("token");
 
 // Build query params for filtering
 let url = `${API}/api/project-progress/jobs/${jobId}/unit-progress`;
 const params = new URLSearchParams();
 if (selectedBuilding !=="all") params.append("building_id", selectedBuilding);
 if (selectedFloor !=="all") params.append("floor", selectedFloor);
 if (params.toString()) url += `?${params.toString()}`;
 
 const response = await axios.get(url, {
 headers: { Authorization: `Bearer ${token}` }
 });
 
 setJob(response.data.job);
 setSubtasks(response.data.subtasks);
 setUnits(response.data.units);
 setBuildings(response.data.buildings);
 setSummary(response.data.summary);
 setTrackingMode(response.data.tracking_mode ||"by_unit");
 
 // Auto-expand all floors initially
 const floors = {};
 response.data.units.forEach(u => {
 const key = `${u.building_id}_${u.floor}`;
 floors[key] = true;
 });
 setExpandedFloors(floors);
 
 } catch (error) {
 console.error("Error loading data:", error);
 toast.error(t('common.error'));
 } finally {
 setLoading(false);
 }
 };

 const handleAddSubtask = async () => {
 if (!newSubtaskName.trim()) {
 toast.error(t('jobSubtasks.enterSubtaskName'));
 return;
 }
 
 try {
 const token = localStorage.getItem("token");
 const response = await axios.post(
 `${API}/api/project-progress/jobs/${jobId}/subtasks`,
 { name: newSubtaskName.trim(), weight: parseFloat(newSubtaskWeight) || 0, apply_to_all_projects: applyToAllProjects },
 { headers: { Authorization: `Bearer ${token}` } }
 );
 
 const propagated = response.data.propagated_to || 0;
 if (propagated > 0) {
 toast.success(t('jobSubtasks.subtaskAddedToAll', { count: propagated }));
 } else {
 toast.success(t('jobSubtasks.subtaskAdded'));
 }
 setNewSubtaskName("");
 setNewSubtaskWeight("");
 setAddSubtaskOpen(false);
 loadData();
 } catch (error) {
 console.error("Error adding subtask:", error);
 toast.error(t('common.error'));
 }
 };

 const handleEditSubtask = async () => {
 if (!editingSubtask || !editingSubtask.name.trim()) return;
 
 try {
 const token = localStorage.getItem("token");
 await axios.put(
 `${API}/api/project-progress/jobs/${jobId}/subtasks/${editingSubtask.id}`,
 { name: editingSubtask.name.trim(), weight: parseFloat(editingSubtask.weight) || 0 },
 { headers: { Authorization: `Bearer ${token}` } }
 );
 
 toast.success(t('jobSubtasks.subtaskUpdated'));
 setEditSubtaskOpen(false);
 setEditingSubtask(null);
 loadData();
 } catch (error) {
 console.error("Error updating subtask:", error);
 toast.error(t('common.error'));
 }
 };

 const handleDeleteSubtask = async (subtaskId) => {
 if (!window.confirm(t('jobSubtasks.confirmDeleteSubtask'))) return;
 
 try {
 const token = localStorage.getItem("token");
 await axios.delete(
 `${API}/api/project-progress/jobs/${jobId}/subtasks/${subtaskId}`,
 { headers: { Authorization: `Bearer ${token}` } }
 );
 
 toast.success(t('jobSubtasks.subtaskDeleted'));
 loadData();
 } catch (error) {
 console.error("Error deleting subtask:", error);
 toast.error(t('common.error'));
 }
 };

 const handleToggleProgress = async (unitId, subtaskId, currentValue) => {
 try {
 const token = localStorage.getItem("token");
 await axios.put(
 `${API}/api/project-progress/jobs/${jobId}/unit-progress/${unitId}/${subtaskId}`,
 { completed: !currentValue },
 { headers: { Authorization: `Bearer ${token}` } }
 );
 
 // Update local state immediately for better UX
 setUnits(prev => prev.map(unit => {
 if (unit.unit_id === unitId) {
 const newSubtasks = { ...unit.subtasks };
 newSubtasks[subtaskId] = { ...newSubtasks[subtaskId], completed: !currentValue };

 // Use weighted progress if subtasks have weights
 const totalWeight = subtasks.reduce((s, st) => s + (st.weight || 0), 0);
 const useWeights = totalWeight > 0;
 let pct = 0;
 if (useWeights) {
 const doneWeight = subtasks.reduce((s, st) => s + (newSubtasks[st.id]?.completed ? (st.weight || 0) : 0), 0);
 pct = Math.round(doneWeight / totalWeight * 100 * 10) / 10;
 } else {
 const completedCount = Object.values(newSubtasks).filter(s => s.completed).length;
 const n = Object.keys(newSubtasks).length;
 pct = n > 0 ? Math.round((completedCount / n) * 100 * 10) / 10 : 0;
 }
 return { ...unit, subtasks: newSubtasks, progress_percent: pct };
 }
 return unit;
 }));
 
 // Update summary
 setSummary(prev => ({
 ...prev,
 total_completed: prev.total_completed + (currentValue ? -1 : 1),
 overall_progress: Math.round(((prev.total_completed + (currentValue ? -1 : 1)) / prev.total_possible) * 100 * 10) / 10
 }));
 
 } catch (error) {
 console.error("Error updating progress:", error);
 toast.error(t('common.error'));
 loadData(); // Reload on error to sync state
 }
 };

 const handleBulkUpdate = async (unitIds, subtaskId, completed) => {
 try {
 const token = localStorage.getItem("token");
 await axios.post(
 `${API}/api/project-progress/jobs/${jobId}/bulk-update`,
 { unit_ids: unitIds, subtask_id: subtaskId, completed },
 { headers: { Authorization: `Bearer ${token}` } }
 );
 
 toast.success(t('jobSubtasks.bulkUpdateSuccess', { count: unitIds.length }));
 loadData();
 } catch (error) {
 console.error("Error bulk updating:", error);
 toast.error(t('common.error'));
 }
 };

 const handleSyncToAllProjects = async () => {
 if (!window.confirm(t('jobSubtasks.confirmSyncAll'))) return;
 
 try {
 const token = localStorage.getItem("token");
 const response = await axios.post(
 `${API}/api/project-progress/jobs/${jobId}/sync-subtasks`,
 {},
 { headers: { Authorization: `Bearer ${token}` } }
 );
 
 const { synced_projects, synced_subtasks } = response.data;
 if (synced_subtasks > 0) {
 toast.success(t('jobSubtasks.syncSuccess', { projects: synced_projects, subtasks: synced_subtasks }));
 } else {
 toast.info(t('jobSubtasks.syncAlreadyDone'));
 }
 } catch (error) {
 console.error("Error syncing subtasks:", error);
 toast.error(t('common.error'));
 }
 };

 const toggleFloor = (buildingId, floor) => {
 const key = `${buildingId}_${floor}`;
 setExpandedFloors(prev => ({
 ...prev,
 [key]: !prev[key]
 }));
 };

 // Get unique floors for filter
 const getUniqueFloors = () => {
 const floors = new Set();
 units.forEach(u => floors.add(u.floor));
 return Array.from(floors).sort((a, b) => a - b);
 };

 // Get unique buildings for filter
 const getUniqueBuildings = () => {
 const buildingMap = {};
 units.forEach(u => {
 if (!buildingMap[u.building_id]) {
 buildingMap[u.building_id] = u.building_name;
 }
 });
 return Object.entries(buildingMap).map(([id, name]) => ({ id, name }));
 };

 // Group units by building and floor
 const getGroupedUnits = () => {
 const grouped = {};
 units.forEach(unit => {
 const buildingKey = unit.building_id || 'no_building';
 const floorKey = unit.floor;
 
 if (!grouped[buildingKey]) {
 grouped[buildingKey] = {
 building_name: unit.building_name,
 floors: {}
 };
 }
 
 if (!grouped[buildingKey].floors[floorKey]) {
 grouped[buildingKey].floors[floorKey] = [];
 }
 
 grouped[buildingKey].floors[floorKey].push(unit);
 });
 
 return grouped;
 };

 // Translate job name
 const translateJobName = (jobName) => {
 const translatedName = t(`projectProgress.jobNames.${jobName}`, { defaultValue: null });
 return translatedName || jobName;
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-sm text-slate-500">{t('common.loading')}</div>
 </div>
 </Layout>
 );
 }

 const groupedUnits = getGroupedUnits();

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 p-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-4">
 <Button 
 variant="outline"
 size="sm"
 data-testid="back-to-progress-btn"
 onClick={() => navigate(`/project-progress?project=${projectId}`)}
 >
 <ArrowLeft className="w-4 h-4 mr-2"/>
 {t('common.back')}
 </Button>
 <div>
 <h1 className="text-2xl font-bold text-slate-900"data-testid="job-subtasks-title">
 {job ? translateJobName(job.job_name) : t('jobSubtasks.title')}
 </h1>
 <p className="text-slate-500 text-sm mt-1">
 {t('jobSubtasks.subtitle')}
 </p>
 </div>
 </div>
 
 {["owner","management","engineer"].includes(user.role) && (
 <div className="flex items-center gap-2">
 {subtasks.length > 0 && ["owner","management"].includes(user.role) && (
 <Button 
 variant="outline"
 data-testid="sync-to-all-btn"
 onClick={handleSyncToAllProjects}
 >
 <RefreshCw className="w-4 h-4 mr-2"/>
 {t('jobSubtasks.syncToAllProjects')}
 </Button>
 )}
 <Button data-testid="add-subtask-btn"onClick={() => setAddSubtaskOpen(true)}>
 <Plus className="w-4 h-4 mr-2"/>
 {t('jobSubtasks.addSubtask')}
 </Button>
 </div>
 )}
 </div>

 {/* Summary Cards */}
 {summary && (
 <div className="grid grid-cols-4 gap-4">
 <Card className="rounded-sm border border-slate-200">
 <CardContent className="pt-4">
 <div className="text-sm text-blue-900 font-medium mb-1">{t('jobSubtasks.overallProgress')}</div>
 <div className="text-2xl font-semibold tabular-nums text-blue-700">{summary.overall_progress}%</div>
 <Progress value={summary.overall_progress} className="mt-2 h-2"/>
 </CardContent>
 </Card>
 
 <Card className="rounded-sm border border-slate-200">
 <CardContent className="pt-4">
 <div className="text-sm text-green-900 font-medium mb-1">{t('jobSubtasks.completedTasks')}</div>
 <div className="text-2xl font-semibold tabular-nums text-green-700">{summary.total_completed}</div>
 <div className="text-[11px] text-slate-500 mt-1">
 {t('jobSubtasks.ofTotal', { total: summary.total_possible })}
 </div>
 </CardContent>
 </Card>
 
 <Card className="rounded-sm border border-slate-200">
 <CardContent className="pt-4">
 <div className="text-sm text-purple-900 font-medium mb-1">
 {trackingMode ==="by_floor"? t('jobSubtasks.totalFloors') : t('jobSubtasks.totalUnits')}
 </div>
 <div className="text-2xl font-semibold tabular-nums text-purple-700">{summary.total_units}</div>
 </CardContent>
 </Card>
 
 <Card className="rounded-sm border border-slate-200">
 <CardContent className="pt-4">
 <div className="text-sm text-orange-900 font-medium mb-1">{t('jobSubtasks.subtasksDefined')}</div>
 <div className="text-2xl font-semibold tabular-nums text-orange-700">{summary.total_subtasks}</div>
 </CardContent>
 </Card>
 </div>
 )}

 {/* Sub-tasks List */}
 {subtasks.length > 0 && (
 <Card className="rounded-sm border border-slate-200">
 <CardHeader className="pb-3">
 <CardTitle className="text-base flex items-center gap-3">
 {t('jobSubtasks.definedSubtasks')}
 {(() => {
 const total = subtasks.reduce((s, st) => s + (st.weight || 0), 0);
 return total > 0 ? (
 <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
 total === 100 ? 'bg-emerald-50 text-emerald-700' : total > 100 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
 }`} data-testid="total-weight-badge">
 Total: {total}%
 </span>
 ) : null;
 })()}
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="flex flex-wrap gap-2">
 {subtasks.map((subtask, index) => (
 <div 
 key={subtask.id}
 className="flex items-center gap-2 px-3 py-2 bg-white/60 rounded-sm"
 >
 <span className="w-6 h-6 flex items-center justify-center bg-blue-600 text-white text-xs rounded-full font-medium">
 {index + 1}
 </span>
 <span className="text-sm font-medium">{subtask.name}</span>
 {subtask.weight > 0 && (
 <span className="text-[11px] text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded">{subtask.weight}%</span>
 )}
 {["owner","management"].includes(user.role) && (
 <div className="flex gap-1 ml-2">
 <Button
 size="sm"
 variant="ghost"
 className="h-6 w-6 p-0"
 onClick={() => {
 setEditingSubtask(subtask);
 setEditSubtaskOpen(true);
 }}
 >
 <Edit2 className="w-3 h-3"/>
 </Button>
 <Button
 size="sm"
 variant="ghost"
 className="h-6 w-6 p-0 text-red-600"
 onClick={() => handleDeleteSubtask(subtask.id)}
 >
 <Trash2 className="w-3 h-3"/>
 </Button>
 </div>
 )}
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Filters */}
 <div className="flex items-center gap-4">
 <div className="flex items-center gap-2">
 <Filter className="w-4 h-4 text-indigo-600"/>
 <span className="text-sm text-slate-500">{t('common.filter')}:</span>
 </div>
 
 <Select value={selectedBuilding} onValueChange={setSelectedBuilding}>
 <SelectTrigger className="w-48">
 <Building2 className="w-4 h-4 mr-2"/>
 <SelectValue placeholder={t('jobSubtasks.allBuildings')} />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">{t('jobSubtasks.allBuildings')}</SelectItem>
 {getUniqueBuildings().map(b => (
 <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 
 {trackingMode !=="by_floor"&& (
 <Select value={selectedFloor} onValueChange={setSelectedFloor}>
 <SelectTrigger className="w-40">
 <Layers className="w-4 h-4 mr-2"/>
 <SelectValue placeholder={t('jobSubtasks.allFloors')} />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">{t('jobSubtasks.allFloors')}</SelectItem>
 {getUniqueFloors().map(floor => (
 <SelectItem key={floor} value={floor.toString()}>
 {t('units.floor')} {floor}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 )}
 </div>

 {/* No subtasks message */}
 {subtasks.length === 0 ? (
 <Card>
 <CardContent className="text-center py-12">
 <CheckSquare className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-600 mb-2">{t('jobSubtasks.noSubtasks')}</p>
 <p className="text-sm text-slate-400 mb-4">{t('jobSubtasks.noSubtasksHint')}</p>
 {["owner","management","engineer"].includes(user.role) && (
 <Button onClick={() => setAddSubtaskOpen(true)}>
 <Plus className="w-4 h-4 mr-2"/>
 {t('jobSubtasks.addFirstSubtask')}
 </Button>
 )}
 </CardContent>
 </Card>
 ) : (
 /* Progress Matrix - Grouped by Building */
 <div className="space-y-4">
 {Object.entries(groupedUnits).map(([buildingId, buildingData]) => (
 <Card key={buildingId} className="rounded-sm border border-slate-200 overflow-hidden">
 <CardHeader className="pb-2">
 <div className="flex items-center justify-between">
 <CardTitle className="flex items-center gap-2">
 <Building2 className="w-5 h-5 text-blue-600"/>
 {buildingData.building_name}
 </CardTitle>
 {buildings.find(b => b.building_name === buildingData.building_name) && (
 <Badge variant="outline"className="bg-blue-500/10 text-blue-700">
 {buildings.find(b => b.building_name === buildingData.building_name)?.progress_percent || 0}% {t('common.completed')}
 </Badge>
 )}
 </div>
 </CardHeader>
 <CardContent>
 {trackingMode ==="by_floor"? (
 /* Floor-based mode: flat table with floors as rows */
 <div className="overflow-x-auto border border-slate-200 rounded-sm">
 <table className="w-full text-sm">
 <thead className="bg-white/50 sticky top-0">
 <tr>
 <th className="text-left p-3 font-medium text-[#334155] border-b min-w-[100px]">
 {t('units.floor')}
 </th>
 {subtasks.map(subtask => (
 <th 
 key={subtask.id} 
 className="text-center p-3 font-medium text-[#334155] border-b min-w-[100px]"
 >
 <div>{subtask.name}</div>
 {subtask.weight > 0 && (
 <div className="text-xs text-slate-400 font-normal">{subtask.weight}%</div>
 )}
 </th>
 ))}
 <th className="text-center p-3 font-medium text-[#334155] border-b min-w-[80px]">
 {t('jobSubtasks.progress')}
 </th>
 </tr>
 </thead>
 <tbody>
 {Object.entries(buildingData.floors)
 .sort(([a], [b]) => Number(a) - Number(b))
 .map(([floor, floorRows]) => 
 floorRows.map((unit, unitIndex) => (
 <tr 
 key={unit.unit_id}
 className={unitIndex % 2 === 0 ? 'bg-white' : 'bg-white/50'}
 >
 <td className="p-3 font-medium text-slate-900 border-b">
 {unit.unit_code}
 </td>
 {subtasks.map(subtask => {
 const progress = unit.subtasks[subtask.id];
 const isCompleted = progress?.completed || false;
 
 return (
 <td key={subtask.id} className="text-center p-3 border-b">
 <button
 className={`w-8 h-8 rounded-full mx-auto flex items-center justify-center transition-all ${
 isCompleted
 ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm'
 : 'bg-slate-100 hover:bg-slate-200 text-slate-400'
 }`}
 onClick={() => handleToggleProgress(unit.unit_id, subtask.id, isCompleted)}
 title={isCompleted ? t('jobSubtasks.markIncomplete') : t('jobSubtasks.markComplete')}
 >
 {isCompleted ? (
 <CheckCircle2 className="w-4 h-4"/>
 ) : (
 <Circle className="w-4 h-4"/>
 )}
 </button>
 </td>
 );
 })}
 <td className="text-center p-3 border-b">
 <div className="flex items-center justify-center gap-2">
 <Progress 
 value={unit.progress_percent} 
 className="w-16 h-2"
 />
 <span className={`text-xs font-medium ${
 unit.progress_percent === 100 
 ? 'text-green-600' 
 : unit.progress_percent > 0 
 ? 'text-blue-600' 
 : 'text-slate-400 '
 }`}>
 {unit.progress_percent}%
 </span>
 </div>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>
 ) : (
 /* Unit-based mode: grouped by floor with accordion */
 <>
 {Object.entries(buildingData.floors)
 .sort(([a], [b]) => Number(a) - Number(b))
 .map(([floor, floorUnits]) => {
 const floorKey = `${buildingId}_${floor}`;
 const isExpanded = expandedFloors[floorKey] !== false;
 
 return (
 <div key={floorKey} className="mb-4 last:mb-0">
 {/* Floor Header */}
 <button
 className="w-full flex items-center justify-between p-2 bg-slate-50 hover:bg-slate-100 transition-colors text-left border border-slate-200 rounded-sm"
 onClick={() => toggleFloor(buildingId, floor)}
 >
 <div className="flex items-center gap-2">
 {isExpanded ? (
 <ChevronDown className="w-4 h-4 text-slate-400"/>
 ) : (
 <ChevronRight className="w-4 h-4 text-slate-400"/>
 )}
 <Layers className="w-4 h-4 text-slate-500"/>
 <span className="font-medium text-sm text-slate-700">
 {t('units.floor')} {floor}
 </span>
 <Badge variant="outline"className="ml-2 text-xs">
 {floorUnits.length} {t('units.units')}
 </Badge>
 </div>
 </button>
 
 {/* Floor Units Table */}
 {isExpanded && (
 <div className="overflow-x-auto border border-t-0 border-slate-200 rounded-sm">
 <table className="w-full text-sm">
 <thead className="bg-white/50 sticky top-0">
 <tr>
 <th className="text-left p-3 font-medium text-[#334155] border-b min-w-[100px]">
 {t('units.unit')}
 </th>
 {subtasks.map(subtask => (
 <th 
 key={subtask.id} 
 className="text-center p-3 font-medium text-[#334155] border-b min-w-[100px]"
 >
 <div>{subtask.name}</div>
 {subtask.weight > 0 && (
 <div className="text-xs text-slate-400 font-normal">{subtask.weight}%</div>
 )}
 </th>
 ))}
 <th className="text-center p-3 font-medium text-[#334155] border-b min-w-[80px]">
 {t('jobSubtasks.progress')}
 </th>
 </tr>
 </thead>
 <tbody>
 {floorUnits.map((unit, unitIndex) => (
 <tr 
 key={unit.unit_id}
 className={unitIndex % 2 === 0 ? 'bg-white' : 'bg-white/50'}
 >
 <td className="p-3 font-medium text-slate-900 border-b">
 {unit.unit_code}
 </td>
 {subtasks.map(subtask => {
 const progress = unit.subtasks[subtask.id];
 const isCompleted = progress?.completed || false;
 
 return (
 <td key={subtask.id} className="text-center p-3 border-b">
 <button
 className={`w-8 h-8 rounded-full mx-auto flex items-center justify-center transition-all ${
 isCompleted
 ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm'
 : 'bg-slate-100 hover:bg-slate-200 text-slate-400'
 }`}
 onClick={() => handleToggleProgress(unit.unit_id, subtask.id, isCompleted)}
 title={isCompleted ? t('jobSubtasks.markIncomplete') : t('jobSubtasks.markComplete')}
 >
 {isCompleted ? (
 <CheckCircle2 className="w-4 h-4"/>
 ) : (
 <Circle className="w-4 h-4"/>
 )}
 </button>
 </td>
 );
 })}
 <td className="text-center p-3 border-b">
 <div className="flex items-center justify-center gap-2">
 <Progress 
 value={unit.progress_percent} 
 className="w-16 h-2"
 />
 <span className={`text-xs font-medium ${
 unit.progress_percent === 100 
 ? 'text-green-600' 
 : unit.progress_percent > 0 
 ? 'text-blue-600' 
 : 'text-slate-400 '
 }`}>
 {unit.progress_percent}%
 </span>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 );
 })}
 </>
 )}
 </CardContent>
 </Card>
 ))}
 </div>
 )}

 {/* Add Subtask Dialog */}
 <Dialog open={addSubtaskOpen} onOpenChange={setAddSubtaskOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{t('jobSubtasks.addSubtask')}</DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <div>
 <Label>{t('jobSubtasks.subtaskName')}</Label>
 <Input
 data-testid="subtask-name-input"
 value={newSubtaskName}
 onChange={(e) => setNewSubtaskName(e.target.value)}
 placeholder={t('jobSubtasks.subtaskNamePlaceholder')}
 onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
 />
 </div>
 <div>
 <Label>{t('jobSubtasks.weightPercent')}</Label>
 <Input
 data-testid="subtask-weight-input"
 type="number"
 min="0"
 max="100"
 value={newSubtaskWeight}
 onChange={(e) => setNewSubtaskWeight(e.target.value)}
 placeholder="e.g., 25"
 />
 </div>
 <div className="flex items-start gap-3 p-3 bg-blue-50/80 rounded-sm border border-blue-200/60">
 <Checkbox
 id="applyToAll"
 data-testid="apply-to-all-checkbox"
 checked={applyToAllProjects}
 onCheckedChange={setApplyToAllProjects}
 className="mt-0.5"
 />
 <div>
 <Label htmlFor="applyToAll"className="text-sm font-medium cursor-pointer">
 {t('jobSubtasks.applyToAllProjects')}
 </Label>
 <p className="text-[11px] text-slate-500 mt-1">
 {t('jobSubtasks.applyToAllProjectsHint', { jobName: job ? translateJobName(job.job_name) : '' })}
 </p>
 </div>
 </div>
 </div>
 <DialogFooter>
 <Button variant="outline"onClick={() => setAddSubtaskOpen(false)}>
 {t('common.cancel')}
 </Button>
 <Button data-testid="confirm-add-subtask-btn"onClick={handleAddSubtask}>
 {t('common.add')}
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Edit Subtask Dialog */}
 <Dialog open={editSubtaskOpen} onOpenChange={setEditSubtaskOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{t('jobSubtasks.editSubtask')}</DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <div>
 <Label>{t('jobSubtasks.subtaskName')}</Label>
 <Input
 value={editingSubtask?.name ||""}
 onChange={(e) => setEditingSubtask(prev => ({ ...prev, name: e.target.value }))}
 placeholder={t('jobSubtasks.subtaskNamePlaceholder')}
 onKeyDown={(e) => e.key === 'Enter' && handleEditSubtask()}
 />
 </div>
 <div>
 <Label>{t('jobSubtasks.weightPercent')}</Label>
 <Input
 type="number"
 min="0"
 max="100"
 value={editingSubtask?.weight ?? ""}
 onChange={(e) => setEditingSubtask(prev => ({ ...prev, weight: e.target.value }))}
 placeholder="e.g., 15"
 data-testid="edit-subtask-weight-input"
 />
 {editingSubtask && (() => {
 const otherTotal = subtasks.filter(s => s.id !== editingSubtask.id).reduce((s, st) => s + (st.weight || 0), 0);
 const newTotal = otherTotal + (parseFloat(editingSubtask.weight) || 0);
 return (
 <p className={`text-xs mt-1 ${newTotal === 100 ? 'text-emerald-600' : newTotal > 100 ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
 Total: {newTotal}% {newTotal === 100 ? '(perfect)' : newTotal > 100 ? '(exceeds 100%!)' : `(${100 - newTotal}% remaining)`}
 </p>
 );
 })()}
 </div>
 </div>
 <DialogFooter>
 <Button variant="outline"onClick={() => setEditSubtaskOpen(false)}>
 {t('common.cancel')}
 </Button>
 <Button onClick={handleEditSubtask}>
 {t('common.save')}
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default JobSubtasksPage;
